import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fetchOrdersPerUser, fetchActivity } from '../../services/dashboardAPI';
import type {
  DashboardOrdersPerUserResponse,
  DashboardActivityResponse,
  ActivitySlaDailyRow,
} from '../../schemas/apiResponse.schemas';
import '../receiving/PrintLabelModal.css';
import './AdminDashboard.css';

type DatePreset = 7 | 30 | 90;

interface DashboardCacheEntry {
  readonly data: DashboardOrdersPerUserResponse;
  readonly activity: DashboardActivityResponse;
}

function dashboardCacheKey(start: string, end: string): string {
  return `${start}|${end}`;
}

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function subtractDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() - days);
  return next;
}

function getPresetRange(days: DatePreset): { start: string; end: string } {
  const end = new Date();
  const start = subtractDays(end, days - 1);
  return {
    start: formatDateInputValue(start),
    end: formatDateInputValue(end),
  };
}

interface WorkerActivitySummary {
  readonly userId: string;
  readonly userName: string;
  readonly scans: number;
  readonly failedScans: number;
  readonly submits: number;
  readonly ordersWorked: number;
  readonly activeSeconds: number;
  readonly linesSubmitted: number;
  readonly pickNextShare: number | null;
  readonly avgSecondsPerLine: number | null;
  readonly cyclesMeasured: number;
}

const DATA_QUALITY_LABELS: Record<string, string> = {
  events_total: 'Events recorded',
  events_null_user: 'Events with no worker',
  non_direct_attribution: 'Inferred/system attribution',
  submits_missing_line_count: 'Submits missing line count',
  scans_missing_sku: 'Scans missing SKU',
  submits_without_prior_scan: 'Submits without a prior scan',
};

/** Checks where a non-zero value indicates a data problem (events_total is informational). */
const DATA_QUALITY_PROBLEM_CHECKS = new Set([
  'events_null_user',
  'submits_missing_line_count',
  'scans_missing_sku',
  'submits_without_prior_scan',
]);

function formatClearTime(isoTimestamp: string | null): string {
  if (!isoTimestamp) return '—';
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatSeconds(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}m ${rest}s`;
}

function formatScanSuccessRate(scans: number, failedScans: number): string {
  const attempts = scans + failedScans;
  if (attempts === 0) return '—';
  return `${Math.round((100 * scans) / attempts)}%`;
}

function formatActiveHours(activeSeconds: number): string {
  if (activeSeconds <= 0) return '—';
  return (activeSeconds / 3600).toFixed(1);
}

function formatRatePerHour(count: number, activeSeconds: number): string {
  if (activeSeconds <= 0) return '—';
  const hours = activeSeconds / 3600;
  return (count / hours).toFixed(1);
}

function formatOrderNumberDisplay(orderNumber: string): string {
  const digits = orderNumber.replace(/\D/g, '');
  return digits.length > 0 ? `#${digits}` : orderNumber;
}

function orderNumberDigitsOnly(orderNumber: string): string {
  return orderNumber.replace(/\D/g, '');
}

interface StillOpenOrdersModalProps {
  row: ActivitySlaDailyRow;
  onClose: () => void;
}

function StillOpenOrdersModal({ row, onClose }: StillOpenOrdersModalProps): React.ReactElement {
  const [copiedOrderNumber, setCopiedOrderNumber] = useState<string | null>(null);

  const handleCopy = useCallback(async (orderNumber: string): Promise<void> => {
    const digits = orderNumberDigitsOnly(orderNumber);
    if (!digits) return;
    try {
      await navigator.clipboard.writeText(digits);
      setCopiedOrderNumber(orderNumber);
      window.setTimeout(() => setCopiedOrderNumber(null), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }, []);

  return (
    <div className="pbl-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="pbl-modal admin-dashboard-still-open-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="pbl-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3 className="admin-dashboard-still-open-title">
          Still open — {row.queueDate}
        </h3>
        <p className="admin-dashboard-still-open-subtitle">
          {row.openRemaining} order{row.openRemaining === 1 ? '' : 's'} did not ship on the queue date.
        </p>
        {row.openOrderNumbers.length === 0 ? (
          <p className="admin-dashboard-status">No order numbers available.</p>
        ) : (
          <ul className="admin-dashboard-still-open-list">
            {row.openOrderNumbers.map((orderNumber) => (
              <li key={orderNumber} className="admin-dashboard-still-open-item">
                <span className="admin-dashboard-still-open-order">
                  {formatOrderNumberDisplay(orderNumber)}
                </span>
                <button
                  type="button"
                  className="admin-dashboard-still-open-copy"
                  onClick={() => void handleCopy(orderNumber)}
                >
                  {copiedOrderNumber === orderNumber ? 'Copied' : 'Copy'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function summarizeWorkerActivity(activity: DashboardActivityResponse): WorkerActivitySummary[] {
  const byUser = new Map<string, WorkerActivitySummary>();

  for (const row of activity.daily) {
    const existing = byUser.get(row.userId);
    byUser.set(row.userId, {
      userId: row.userId,
      userName: row.userName,
      scans: (existing?.scans ?? 0) + row.scans,
      failedScans: (existing?.failedScans ?? 0) + row.failedScans,
      submits: (existing?.submits ?? 0) + row.submits,
      ordersWorked: (existing?.ordersWorked ?? 0) + row.ordersWorked,
      activeSeconds: (existing?.activeSeconds ?? 0) + row.activeSeconds,
      linesSubmitted: (existing?.linesSubmitted ?? 0) + row.linesSubmitted,
      pickNextShare: null,
      avgSecondsPerLine: null,
      cyclesMeasured: 0,
    });
  }

  const cycleAgg = new Map<string, { secondsPerLineSum: number; measured: number; pickNext: number; total: number }>();
  for (const cycle of activity.cycles) {
    const agg = cycleAgg.get(cycle.userId) ?? { secondsPerLineSum: 0, measured: 0, pickNext: 0, total: 0 };
    agg.total += 1;
    if (cycle.assignmentSource === 'pick_next') agg.pickNext += 1;
    if (cycle.secondsPerLine !== null) {
      agg.secondsPerLineSum += cycle.secondsPerLine;
      agg.measured += 1;
    }
    cycleAgg.set(cycle.userId, agg);
  }

  return [...byUser.values()]
    .map((summary) => {
      const agg = cycleAgg.get(summary.userId);
      return {
        ...summary,
        pickNextShare: agg && agg.total > 0 ? agg.pickNext / agg.total : null,
        avgSecondsPerLine: agg && agg.measured > 0 ? agg.secondsPerLineSum / agg.measured : null,
        cyclesMeasured: agg?.measured ?? 0,
      };
    })
    .sort((left, right) => right.submits - left.submits);
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const initialRange = useMemo(() => getPresetRange(30), []);
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [activePreset, setActivePreset] = useState<DatePreset | null>(30);
  const [data, setData] = useState<DashboardOrdersPerUserResponse | null>(null);
  const [activity, setActivity] = useState<DashboardActivityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stillOpenModalRow, setStillOpenModalRow] = useState<ActivitySlaDailyRow | null>(null);
  const dashboardCacheRef = useRef(new Map<string, DashboardCacheEntry>());

  const loadDashboard = useCallback(async (start: string, end: string): Promise<void> => {
    if (!start || !end || start > end) {
      setError('Choose a valid start and end date.');
      return;
    }

    const cacheKey = dashboardCacheKey(start, end);
    const cached = dashboardCacheRef.current.get(cacheKey);
    const hasCachedData = cached !== undefined;

    if (cached) {
      setData(cached.data);
      setActivity(cached.activity);
      setError(null);
    }

    if (hasCachedData) {
      setRefreshing(true);
    } else {
      setLoading(true);
      setError(null);
    }

    try {
      const [ordersResponse, activityResponse] = await Promise.all([
        fetchOrdersPerUser({ start, end }),
        fetchActivity({ start, end }),
      ]);
      dashboardCacheRef.current.set(cacheKey, {
        data: ordersResponse,
        activity: activityResponse,
      });
      setData(ordersResponse);
      setActivity(activityResponse);
      setError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard data';
      setError(message);
      if (!hasCachedData) {
        setData(null);
        setActivity(null);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadDashboard(startDate, endDate);
  }, [startDate, endDate, loadDashboard]);

  const applyPreset = (days: DatePreset): void => {
    const range = getPresetRange(days);
    setActivePreset(days);
    setStartDate(range.start);
    setEndDate(range.end);
  };

  const chartData = useMemo(
    () =>
      (data?.users ?? []).map((user) => ({
        name: user.userName,
        totalOrders: user.totalOrders,
      })),
    [data],
  );

  const workerSummaries = useMemo(
    () => (activity ? summarizeWorkerActivity(activity) : []),
    [activity],
  );

  return (
    <div className="admin-dashboard-container">
      <div className="admin-dashboard-header">
        <button
          type="button"
          className="admin-dashboard-back"
          onClick={() => navigate('/admin')}
        >
          Back to Admin Panel
        </button>
        <p className="admin-dashboard-subtitle">Orders fulfilled per user</p>
      </div>

      <section className="admin-dashboard-section">
        <h2>Time interval</h2>
        <div className="admin-dashboard-presets">
          {([7, 30, 90] as const).map((days) => (
            <button
              key={days}
              type="button"
              className={`admin-dashboard-preset${activePreset === days ? ' active' : ''}`}
              onClick={() => applyPreset(days)}
            >
              Last {days} days
            </button>
          ))}
        </div>
        <div className="admin-dashboard-date-range">
          <label>
            Start
            <input
              type="date"
              value={startDate}
              onChange={(event) => {
                setActivePreset(null);
                setStartDate(event.target.value);
              }}
            />
          </label>
          <label>
            End
            <input
              type="date"
              value={endDate}
              onChange={(event) => {
                setActivePreset(null);
                setEndDate(event.target.value);
              }}
            />
          </label>
        </div>
      </section>

      {loading && <p className="admin-dashboard-status">Loading dashboard data...</p>}
      {refreshing && !loading && (
        <p className="admin-dashboard-status admin-dashboard-refreshing">Refreshing dashboard data...</p>
      )}
      {error && <p className="admin-dashboard-error">{error}</p>}

      {!loading && data && (
        <>
          <section className="admin-dashboard-section">
            <h2>Total orders by user</h2>
            {chartData.length === 0 ? (
              <p className="admin-dashboard-status">No orders found for this interval.</p>
            ) : (
              <div className="admin-dashboard-chart">
                <ResponsiveContainer width="100%" height={360}>
                  <BarChart data={chartData} margin={{ top: 16, right: 16, left: 0, bottom: 64 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#555" />
                    <XAxis
                      dataKey="name"
                      angle={-30}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fill: '#f5f5f5', fontSize: 12 }}
                    />
                    <YAxis allowDecimals={false} tick={{ fill: '#f5f5f5' }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#222', border: '1px solid #B22222' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="totalOrders" fill="#B22222" name="Total orders" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>

          <section className="admin-dashboard-section">
            <h2>User stats</h2>
            {data.users.length === 0 ? (
              <p className="admin-dashboard-status">No users found for this interval.</p>
            ) : (
              <div className="admin-dashboard-table-wrap">
                <table className="admin-dashboard-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Total orders</th>
                      <th>Active days</th>
                      <th>Avg orders / active day</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map((user) => (
                      <tr key={user.userTagId}>
                        <td>{user.userName}</td>
                        <td>{user.totalOrders}</td>
                        <td>{user.activeDays}</td>
                        <td>{user.avgOrdersPerActiveDay.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="admin-dashboard-section">
            <h2>Scan &amp; submit activity</h2>
            <p className="admin-dashboard-subtitle">
              Active hours and throughput rates use inferred fulfillment time: gaps between consecutive
              events are summed with a 15-minute cap per gap (non-fulfillment work is not tracked yet).
            </p>
            {workerSummaries.length === 0 ? (
              <p className="admin-dashboard-status">No activity events found for this interval.</p>
            ) : (
              <div className="admin-dashboard-table-wrap">
                <table className="admin-dashboard-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Scans</th>
                      <th>Failed scans</th>
                      <th>Scan success rate</th>
                      <th>Submits</th>
                      <th>Orders worked</th>
                      <th>Active hours</th>
                      <th>Orders/hr</th>
                      <th>Lines/hr</th>
                      <th>Pick-next share</th>
                      <th>Avg time / line</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workerSummaries.map((summary) => (
                      <tr key={summary.userId}>
                        <td>{summary.userName}</td>
                        <td>{summary.scans}</td>
                        <td>{summary.failedScans}</td>
                        <td>{formatScanSuccessRate(summary.scans, summary.failedScans)}</td>
                        <td>{summary.submits}</td>
                        <td>{summary.ordersWorked}</td>
                        <td>{formatActiveHours(summary.activeSeconds)}</td>
                        <td>{formatRatePerHour(summary.submits, summary.activeSeconds)}</td>
                        <td>{formatRatePerHour(summary.linesSubmitted, summary.activeSeconds)}</td>
                        <td>
                          {summary.pickNextShare === null
                            ? '—'
                            : `${Math.round(summary.pickNextShare * 100)}%`}
                        </td>
                        <td>
                          {formatSeconds(summary.avgSecondsPerLine)}
                          {summary.cyclesMeasured > 0 ? ` (${summary.cyclesMeasured} orders)` : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {activity && (
            <section className="admin-dashboard-section">
              <h2>Daily queue SLA</h2>
              <p className="admin-dashboard-subtitle">
                Cleared orders ship same day when cleared by 1 PM Pacific; later clearance joins the next business day.
                Still open counts orders that did not ship on the queue date (unshipped or late-shipped).
              </p>
              {activity.sla.length === 0 ? (
                <p className="admin-dashboard-status">No daily queues found for this interval.</p>
              ) : (
                <div className="admin-dashboard-table-wrap">
                  <table className="admin-dashboard-table">
                    <thead>
                      <tr>
                        <th>Queue date</th>
                        <th>Orders</th>
                        <th>Expedited</th>
                        <th>Shipped same day</th>
                        <th>Attainment</th>
                        <th>Still open</th>
                        <th>Queue cleared</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.sla.map((row) => (
                        <tr key={row.queueDate}>
                          <td>{row.queueDate}</td>
                          <td>{row.ordersTotal}</td>
                          <td>{row.expeditedTotal}</td>
                          <td>{row.metSameDay}</td>
                          <td className={row.attainmentPct !== null && row.attainmentPct < 100 ? 'admin-dashboard-error' : undefined}>
                            {row.attainmentPct === null ? '—' : `${row.attainmentPct.toFixed(1)}%`}
                          </td>
                          <td className={row.openRemaining > 0 ? 'admin-dashboard-error' : undefined}>
                            {row.openRemaining > 0 ? (
                              <button
                                type="button"
                                className="admin-dashboard-still-open-link"
                                onClick={() => setStillOpenModalRow(row)}
                              >
                                {row.openRemaining}
                              </button>
                            ) : (
                              row.openRemaining
                            )}
                          </td>
                          <td>{formatClearTime(row.queueClearAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {activity && (
            <section className="admin-dashboard-section">
              <h2>Data quality</h2>
              <p className="admin-dashboard-subtitle">
                If these look bad, don&apos;t trust the metrics above.
              </p>
              <div className="admin-dashboard-table-wrap">
                <table className="admin-dashboard-table">
                  <thead>
                    <tr>
                      <th>Check</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.dataQuality.map((check) => {
                      const isProblem =
                        DATA_QUALITY_PROBLEM_CHECKS.has(check.checkName) && check.value > 0;
                      return (
                        <tr key={check.checkName}>
                          <td>{DATA_QUALITY_LABELS[check.checkName] ?? check.checkName}</td>
                          <td className={isProblem ? 'admin-dashboard-error' : undefined}>
                            {check.value}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      {stillOpenModalRow && (
        <StillOpenOrdersModal
          row={stillOpenModalRow}
          onClose={() => setStillOpenModalRow(null)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
